import { apiRequest, fetchAuthenticatedBlob } from './client';

export type WearingStatus = 'waiting_start' | 'wearing' | 'finishing' | 'awaiting_final_photos' | 'result_review' | 'result_reviewed';
export type WearingBoardItem = {
  readonly patient_id: string;
  readonly patient_name: string;
  readonly treatment_id: string;
  readonly cycle_id: string;
  readonly cycle_number: number;
  readonly manager_id: string | null;
  readonly manager_ids?: readonly string[];
  readonly manager_name?: string | null;
  readonly status: WearingStatus;
  readonly predicted_finish_date: string | null;
  readonly days_remaining: number | null;
  readonly paused: boolean;
  readonly overdue_photos: boolean;
  readonly retake_requested: boolean;
  readonly last_photo_date?: string | null;
  readonly jaws: readonly { jaw_type: string; current?: number | null; completed: number; total: number }[];
};
export type WearingBoardResponse = {
  readonly items: readonly WearingBoardItem[];
  readonly counts: Partial<Record<WearingStatus, number>>;
};
export type WearingBoardFilters = {
  readonly manager_id?: string;
  readonly date_from?: string;
  readonly date_to?: string;
  readonly finishing_within_days?: number;
  readonly paused?: boolean;
  readonly overdue_photos?: boolean;
  readonly needs_review?: boolean;
};
export type ResultScope = 'cycle' | 'treatment';
export type ResultComparison = {
  readonly final_entry_id: string | null;
  readonly baseline_entry_id: string | null;
  readonly review_status: string | null;
  readonly collage_status: string | null;
  readonly collage_url: string | null;
  readonly paired_angles: readonly {
    angle_type: string;
    before_url: string | null;
    after_url: string | null;
    before_date: string | null;
    after_date: string | null;
  }[];
  readonly required_angles?: readonly string[];
  readonly missing_angles: readonly string[];
  readonly retake_angles: readonly string[];
  readonly review_comment: string | null;
  readonly reviewed_at: string | null;
  readonly viewed_at?: string | null;
};

export function getWearingBoard(filters: WearingBoardFilters, token: string): Promise<WearingBoardResponse> {
  const params = new URLSearchParams();
  if (filters.manager_id) params.set('manager_id', filters.manager_id);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (filters.finishing_within_days !== undefined) params.set('finishing_within_days', String(filters.finishing_within_days));
  if (filters.paused !== undefined) params.set('paused', String(filters.paused));
  if (filters.overdue_photos !== undefined) params.set('overdue_photos', String(filters.overdue_photos));
  if (filters.needs_review !== undefined) params.set('needs_review', String(filters.needs_review));
  return apiRequest<WearingBoardResponse>(`/admin/wearing-board?${params}`, { token });
}

function resultPath(patientId: string, cycleId: string): string {
  return `/admin/patients/${encodeURIComponent(patientId)}/treatment-cycles/${encodeURIComponent(cycleId)}/result`;
}

export function getResultComparison(patientId: string, cycleId: string, scope: ResultScope, token: string): Promise<ResultComparison> {
  return apiRequest<ResultComparison>(`${resultPath(patientId, cycleId)}?scope=${scope}`, { token });
}

export function markResultViewed(patientId: string, cycleId: string, token: string): Promise<{ viewed_at: string }> {
  return apiRequest<{ viewed_at: string }>(`${resultPath(patientId, cycleId)}/viewed`, { method: 'POST', token });
}

export function requestResultRetake(patientId: string, cycleId: string, angles: readonly string[], comment: string, token: string): Promise<Partial<ResultComparison>> {
  return apiRequest<Partial<ResultComparison>>(`${resultPath(patientId, cycleId)}/retake`, { method: 'POST', body: { angles, comment }, token });
}

export function decideResult(patientId: string, cycleId: string, decision: 'approved' | 'requires_followup', comment: string, token: string): Promise<Partial<ResultComparison>> {
  return apiRequest<Partial<ResultComparison>>(`${resultPath(patientId, cycleId)}/decision`, { method: 'POST', body: { decision, comment: comment || undefined }, token });
}

export function fetchResultCollage(patientId: string, cycleId: string, scope: ResultScope, token: string): Promise<Blob> {
  return fetchAuthenticatedBlob(`${resultPath(patientId, cycleId)}/collage?scope=${scope}`, token);
}
